"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";

// Illustrative assumption only (labeled as an example on the page) — not any specific vendor's price.
const EXAMPLE_KIOSK_PRICE = 1500;

export default function KioskSavingsScroll() {
  const { t } = useLang();
  const [lanes, setLanes] = useState(3);

  const dedicatedTotal = lanes * EXAMPLE_KIOSK_PRICE;

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Multi-lane math", "Matemática multi-kiosko")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("How many lanes ", "¿Cuántos kioskos ")}
            <span className="text-redy-charcoal/30">{t("do you need?", "necesitas?")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Drag the slider. Dedicated kiosk hardware and licenses add up fast. Redy runs on an iPad you already own — no extra kiosk fee.",
              "Mueve el slider. El hardware y las licencias de kiosko suman rápido. Redy corre en un iPad que ya tienes — sin fee extra de kiosko."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-[#FAFAF8] border border-black/5 rounded-[32px] p-8 md:p-12 max-w-[820px] mx-auto"
        >
          {/* Lane count */}
          <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-3">
            {t("Number of kiosk lanes", "Número de kioskos")}
          </div>
          <div className="flex items-end justify-between mb-3">
            <div className="text-[56px] font-black text-redy-charcoal leading-none tabular-nums">
              {lanes}
            </div>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div
                  key={n}
                  className="w-8 h-8 flex items-center justify-center"
                  style={{ color: n === lanes ? "#F4722B" : "rgba(0,0,0,0.15)" }}
                >
                  <Tablet />
                </div>
              ))}
            </div>
          </div>
          <input
            type="range"
            min={1}
            max={6}
            step={1}
            value={lanes}
            onChange={(e) => setLanes(Number(e.target.value))}
            className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gradient-to-r from-[#F4722B] to-[#D7263D]/30 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-[3px] [&::-webkit-slider-thumb]:border-[#F4722B] [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-grab active:[&::-webkit-slider-thumb]:cursor-grabbing"
          />
          <div className="flex justify-between text-[11px] font-bold text-redy-secondary/50 mt-2">
            <span>1</span>
            <span>6+</span>
          </div>

          <div className="h-px bg-black/5 my-10" />

          {/* Compare */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-3">
                {t("Dedicated kiosks (example)", "Kioskos dedicados (ejemplo)")}
              </div>
              <div className="text-[40px] md:text-[52px] font-black tracking-tight text-redy-charcoal/40 leading-none tabular-nums">
                ${dedicatedTotal.toLocaleString()}
              </div>
              <div className="text-[12px] font-medium text-redy-secondary mt-2">
                {t(
                  `Example: assumes $${EXAMPLE_KIOSK_PRICE.toLocaleString()} of dedicated hardware per lane. Real prices vary.`,
                  `Ejemplo: supone $${EXAMPLE_KIOSK_PRICE.toLocaleString()} de hardware dedicado por kiosko. Los precios reales varían.`
                )}
              </div>
            </div>

            <div>
              <div className="text-[10px] font-black tracking-widest uppercase mb-3" style={{ color: "#D7263D" }}>
                {t("Redy charges", "Redy cobra")}
              </div>
              <div
                className="text-[40px] md:text-[52px] font-black tracking-tight leading-none tabular-nums"
                style={{
                  background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                $0
              </div>
              <div className="text-[12px] font-medium text-redy-secondary mt-2">
                {t("Use iPads you already own. No extra kiosk fee.", "Usa iPads que ya tienes. Sin fee extra de kiosko.")}
              </div>
            </div>
          </div>

          {/* Savings banner */}
          <div
            className="rounded-[20px] px-6 py-6 text-white"
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              boxShadow: "0 20px 50px rgba(244,114,43,0.30)",
            }}
          >
            <div className="text-[11px] font-black tracking-widest uppercase text-white/75 mb-2">
              {t("Stays in your bank", "Se queda en tu banco")}
            </div>
            <div className="text-[48px] md:text-[64px] font-black tabular-nums leading-none">
              +${dedicatedTotal.toLocaleString()}
            </div>
            <div className="text-[13px] font-bold text-white/85 mt-1">
              {t("Up front, in this example.", "Por adelantado, en este ejemplo.")}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Tablet() {
  return (
    <svg width="18" height="22" viewBox="0 0 18 22" fill="none">
      <rect x="0.75" y="0.75" width="16.5" height="20.5" rx="2.25" stroke="currentColor" strokeWidth="1.5" />
      <line x1="9" y1="18" x2="9" y2="18.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
